import { useState, useCallback, useRef } from 'react';

/**
 * useColumnResize — هوک مشترک برای resizable کردن ستون‌های هر جدول.
 *
 * نکته‌ی مهم: جدول حتماً باید tableLayout:'fixed' داشته باشه وگرنه مرورگر
 * عرض ستون‌ها رو خودش تعیین می‌کنه و width روی th نادیده گرفته می‌شه.
 *
 * سرستون‌ها خودکار هنگام اسکرول قفل می‌شوند؛ کافی است جدول داخل یک div با
 * overflow:auto و ارتفاع محدود (maxHeight/flex) باشد. استایل sticky را جداگانه
 * در صفحه ننویسید.
 *
 * استفاده:
 *   const { widths, TH } = useColumnResize([120, 80, 200, ...]);
 *   <table style={{ tableLayout:'fixed' }}>
 *     <thead><tr>
 *       <TH idx={0}>عنوان</TH>
 *       <TH idx={1} right>شرح</TH>
 *     </tr></thead>
 */
export default function useColumnResize(initialWidths) {
  const [widths, setWidths] = useState(initialWidths);
  const wRef = useRef(widths);
  wRef.current = widths;

  const startResize = useCallback((idx, e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = wRef.current[idx];

    const onMove = ev => {
      const diff = startX - ev.clientX;  // RTL: کشیدن به چپ = بزرگ‌تر
      setWidths(cur => {
        const n = [...cur];
        n[idx] = Math.max(40, startW + diff);
        return n;
      });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  /* کامپوننت TH — هدر ستون با دستگیره‌ی تغییر عرض */
  const TH = useCallback(({ idx, children, right, gold, red, green, style: extra }) => (
    <th style={{
      width: wRef.current[idx], minWidth: 40, userSelect: 'none',
      /* سرستون هنگام اسکرول عمودی قفل می‌ماند. sticky خودش جای دستگیره‌ی تغییر عرض را هم نگه می‌دارد
         و با پراپ style قابل تغییر است (مثلاً top برای سرستون دوردیفه) */
      position: 'sticky', top: 0, zIndex: 3,
      padding: '9px 10px', fontWeight: '700', whiteSpace: 'nowrap',
      borderLeft: '1px solid rgba(255,255,255,0.1)', fontFamily: 'inherit', fontSize: '12px',
      textAlign: right ? 'right' : 'center',
      background: gold ? '#0d2137' : red ? '#1a3a2a' : green ? '#0d3020' : '#1a2f50',
      color: gold ? '#C8A84B' : red ? '#ef9a9a' : green ? '#a5d6a7' : '#fff',
      ...extra,
    }}>
      {children}
      {idx !== undefined && (
        <span
          onMouseDown={e => startResize(idx, e)}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(200,168,75,0.6)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          style={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: '8px',
            cursor: 'col-resize', background: 'transparent', zIndex: 5,
          }}
        />
      )}
    </th>
  ), [startResize]);

  return { widths, setWidths, TH };
}